import { useCallback, useEffect, useState } from 'react'
import { message } from 'antd'
import { handleError } from '../../../../lib/utils'
import { getInvoices, updateRemittance, getInvoicesWithBillingRecords } from '../utils'
import { readPatients } from '../../../../Admin/patients/utils/utils'
import { readBillingRecords } from '../../../../Bookings/utils/utils'
import { useDispatch, useSelector } from 'react-redux'
import coreReducer from '../../../../../core/utils/reducer'
import CoreEnums from '../../../../../core/utils/enums'
import { debounce } from 'lodash'
import dayjs from 'dayjs'
import { recordRemittancePayment } from '../../../../Billing-Module-Enhanced/backend-integration/api'
import {
  logAuditEvent,
  AuditActions,
  AuditEntityTypes
} from '../../../../Billing-Module-Enhanced/backend-integration/api/auditTrail'

/**
 * Hook for managing medical aid payment functionality
 * @param {Object} data - Initial data for the medical aid payment
 * @param {Function} handleGetRemittances - Function to refresh remittances
 * @returns {Object} Functions and state for medical aid payment
 */
export const useMedicalAidPayment = (data, handleGetRemittances, form) => {
  const dispatch = useDispatch()

  const [loading, setLoading] = useState(false)
  const [submitLoading, setSubmitLoading] = useState(false)
  const [formData, setFormData] = useState(data)
  const [items, setItems] = useState([])
  const [invoiceRef, setInvoiceRef] = useState('')
  const [medicalAidTotal, setMedicalAidTotal] = useState(0)

  const [searchData, setSearchData] = useState([])
  const [searchLoading, setSearchLoading] = useState(false)
  const [searchValue, setSearchValue] = useState('')

  const [invoices, setInvoices] = useState([])
  const [isModalVisible, setIsModalVisible] = useState(false)

  const agiliteUser = useSelector((state) => state.auth.agiliteUser)

  useEffect(() => {
    const totalPayment = items.reduce((acc, item) => acc + parseFloat(item.payment || 0), 0)
    form.setFieldsValue({ paymentAmount: totalPayment.toFixed(2) })
  }, [items, form])

  /**
   * Allocates payment based on form data
   * @param {Object} form - Form instance
   */
  const handleAllocatePayment = async (form) => {
    try {
      await form.validateFields()

      // Validate that an invoice has been selected
      if (!invoiceRef) {
        message.error('Please select an invoice before allocating payment')
        return
      }

      // Get the selected invoice and validate it's not private
      const selectedInvoice = invoices.find((inv) => inv._id === invoiceRef)
      if (
        selectedInvoice?.accountGroup === 'private' ||
        selectedInvoice?.goodXStatus === 'Private' ||
        !selectedInvoice?.goodXTicketNo
      ) {
        message.error('Medical aid payments cannot be allocated to private invoices')
        return
      }

      let tmpPayments = data.payments ? data.payments : []
      let runningTotal = data.runningTotal

      const paymentData = {
        invoiceRef: invoiceRef,
        userRef: form.getFieldValue('account'),
        accStatus: form.getFieldValue('accountStatus'),
        receiptDate: form.getFieldValue('receiptDate'),
        paymentAmount: form.getFieldValue('paymentAmount'),
        createdAt: dayjs(new Date()).format('YYYY-MM-DD HH:mm:ss'),
        createdBy: agiliteUser._id
      }

      tmpPayments.push(paymentData)

      runningTotal = (parseFloat(runningTotal) - parseFloat(form.getFieldValue('paymentAmount'))).toFixed(2)

      setSubmitLoading(true)
      try {
        // Update remittance with the new payment
        await updateRemittance(data._id, {
          runningTotal,
          payments: tmpPayments
        })

        try {
          // Get invoice details
          const selectedInvoice = invoices.find((inv) => inv._id === invoiceRef)
          const patient = searchData.find((p) => p._id === paymentData.userRef)

          console.log('Creating ledger entry for medical aid payment:', {
            patientId: paymentData.userRef,
            billingId: patient?.billingId,
            invoiceRef: invoiceRef,
            selectedInvoice: selectedInvoice,
            amount: paymentData.paymentAmount
          })

          const ledgerData = {
            patientId: paymentData.userRef,
            billingId: patient?.billingId,
            invoiceNo: selectedInvoice?.invoiceNo,
            amount: paymentData.paymentAmount,
            medicalAid: data.medicalAid,
            receiptNumber: data.receiptNumber,
            remittanceDate: data.remittanceDate,
            remittanceId: data._id
          }

          console.log('Ledger data being sent:', ledgerData)

          const result = await recordRemittancePayment(ledgerData)
          console.log('Ledger entry created successfully:', result)

          // Log audit trail for medical aid payment allocation
          await logAuditEvent(
            AuditActions.ALLOCATE,
            AuditEntityTypes.PAYMENT,
            result._id || invoiceRef,
            `Medical aid payment allocated: R${paymentData.paymentAmount} from ${data.medicalAid}`,
            {
              billingId: patient?.billingId,
              medicalAid: data.medicalAid,
              amount: paymentData.paymentAmount,
              invoiceNo: selectedInvoice?.invoiceNo,
              patientId: paymentData.userRef,
              receiptNumber: data.receiptNumber,
              remittanceId: data._id
            }
          )
        } catch (ledgerError) {
          console.error('Failed to create ledger entry for medical aid payment:', ledgerError)
          console.error('Full error details:', {
            message: ledgerError.message,
            stack: ledgerError.stack,
            paymentData: paymentData
          })
          // Show error to user
          message.error(`Failed to record payment in ledger: ${ledgerError.message}`)
        }

        message.success('Medical aid allocation recorded successfully')
        handleCloseTab()
      } catch (error) {
        message.error(handleError(error, true))
      }
      setSubmitLoading(false)
    } catch (e) {
      // Form validation error
    }
  }

  /**
   * Closes the current tab
   */
  const handleCloseTab = () => {
    dispatch(
      coreReducer.actions.closeTab({
        targetKey: `${CoreEnums.tabKeys.PAYMENT_REMITTANCE}`,
        removeBreadcrumb: true
      })
    )
    handleGetRemittances()
  }

  /**
   * Handles changes to form fields
   * @param {Object} changedFields - Fields that have changed
   */
  const handleFieldChange = (changedFields) => {
    try {
      setFormData({ ...formData, ...changedFields })
      setItems([...items])
    } catch (e) {
      message.error(handleError(e, true))
    }
  }

  /**
   * Handles changes to table field values
   * @param {string} prop - Property to change
   * @param {number} index - Index of the item in the items array
   * @param {any} value - New value
   */
  const handleTableFieldChange = (prop, index, value) => {
    const tmpItems = JSON.parse(JSON.stringify(items))
    tmpItems[index][prop] = value

    try {
      // When the payment amount on a row is changed
      if (prop === 'payment') {
        const tmpPayment = value
        const originalMedicalAidAmount = tmpItems[index].oldMedicalAid // Use the original medical aid amount for validation

        if (tmpPayment > parseFloat(originalMedicalAidAmount)) {
          throw new Error('Payment amount cannot be greater than the Medical Aid amount')
        } else {
          tmpItems[index].medicalAid = (parseFloat(originalMedicalAidAmount) - tmpPayment).toFixed(2)
        }
      }

      setItems(tmpItems)
    } catch (e) {
      message.error(e.message)
    }
  }

  /**
   * Generates totals data for the table footer
   * @returns {Array} Array containing totals data
   */
  const generateTotalsData = () => {
    const data = [
      {
        total: items.reduce((a, b) => a + parseFloat(b.total), 0).toFixed(2),
        medicalAid: items.reduce((a, b) => a + parseFloat(b.medicalAid), 0).toFixed(2),
        patient: items.reduce((a, b) => a + parseFloat(b.patient), 0).toFixed(2),
        payment: items.reduce((a, b) => a + parseFloat(b.payment), 0).toFixed(2),
        discount: items.reduce((a, b) => a + parseFloat(b.discount), 0).toFixed(2)
      }
    ]

    for (const entry of data) {
      for (const prop in entry) {
        if (isNaN(entry[prop])) {
          entry[prop] = 0
        }
      }
    }

    return data
  }

  /**
   * Performs search for patients
   */
  const performSearch = async () => {
    try {
      let tmpSearchData = []

      const searchTerms = searchValue.trim().toLowerCase().split(/\s+/)

      // Create regex patterns for partial matching
      const regexPatterns = searchTerms.map((term) => new RegExp(term, 'i'))

      const searchQuery = {
        $or: [
          // Match against concatenated firstName and lastName
          {
            $expr: {
              $regexMatch: {
                input: { $concat: [{ $toLower: '$firstName' }, ' ', { $toLower: '$lastName' }] },
                regex: searchTerms.map((term) => `.*${term}.*`).join(''),
                options: 'i'
              }
            }
          },
          // Match individually against firstName and lastName
          {
            $or: [
              {
                $expr: {
                  $regexMatch: {
                    input: { $toLower: '$firstName' },
                    regex: regexPatterns.join('|'),
                    options: 'i'
                  }
                }
              },
              {
                $expr: {
                  $regexMatch: {
                    input: { $toLower: '$lastName' },
                    regex: regexPatterns.join('|'),
                    options: 'i'
                  }
                }
              }
            ]
          }
        ]
      }

      tmpSearchData = await readPatients(searchQuery)

      setSearchData(tmpSearchData)
      setSearchLoading(false)
    } catch (e) {
      message.error(handleError(e, true))
    }
  }

  // Debounced search function to prevent too many API calls
  // eslint-disable-next-line
  const debouncedSearch = useCallback(
    debounce((query) => {
      if (query) {
        setSearchValue(query)
      }
    }, 2000),
    []
  )

  /**
   * Initiates a search for accounts
   * @param {string} value - Search value
   */
  const handleSearch = (value) => {
    setSearchLoading(true)
    debouncedSearch(value)
  }

  /**
   * Handles account selection and loads invoices
   * @param {string} value - Selected account ID
   */
  const handleAccountChange = async (value) => {
    setLoading(true)

    try {
      if (value) {
        console.log('Loading invoices for user:', value)
        let invoicesWithBillingRecords = await getInvoicesWithBillingRecords({
          userRef: value,
          status: { $ne: 'completed' }
        })

        // Filter out private invoices for medical aid payments
        // Check both accountGroup and goodXStatus (which shows as 'Private' in Switch Status)
        invoicesWithBillingRecords = invoicesWithBillingRecords.filter((inv) => {
          // Exclude if accountGroup is private
          if (inv.accountGroup === 'private') return false

          // Exclude if goodXStatus is 'Private' (means not submitted to medical aid)
          if (inv.goodXStatus === 'Private') return false

          // Exclude if no goodXTicketNo (also means private/not submitted to medical aid)
          if (!inv.goodXTicketNo) return false

          return true
        })
        console.log(
          'Filtered out private invoices for medical aid payment. Remaining invoices:',
          invoicesWithBillingRecords.length
        )

        // Debug: Log complete invoice structure
        console.log('Complete invoices data:', invoicesWithBillingRecords)

        // Debug: Log detailed billing records info
        invoicesWithBillingRecords.forEach((inv, index) => {
          console.log(`Invoice ${index + 1} (${inv.invoiceNo}):`, {
            invoiceId: inv._id,
            bookingRef: inv.bookingRef,
            billingRecordsCount: inv.billingRecords?.length || 0,
            billingRecords: inv.billingRecords,
            firstBillingRecord: inv.billingRecords?.[0]
          })
        })

        setInvoices(invoicesWithBillingRecords)

        if (invoicesWithBillingRecords.length > 0) {
          setIsModalVisible(true)
        } else {
          message.warning('No pending invoices found for this account')
        }
      } else {
        setItems([])
        setInvoices([])
      }
    } catch (e) {
      message.error(handleError(e, true))
    }

    setLoading(false)
  }

  /**
   * Handles invoice selection and loads billing records
   * @param {Object} selectedInvoice - Selected invoice
   */
  const handleInvoiceSelect = async (selectedInvoice) => {
    let tmpItems = []
    setInvoiceRef(selectedInvoice._id)
    setLoading(true)

    try {
      tmpItems = await readBillingRecords({ bookingRef: selectedInvoice.bookingRef })

      for (const entry of tmpItems) {
        entry.total = entry.total ? entry.total : '0'
        entry.medicalAid = entry.medicalAid ? entry.medicalAid : '0'
        entry.patient = entry.patient ? entry.patient : '0'
        entry.payment = entry.payment ? entry.payment : '0'
        entry.discount = entry.discount ? entry.discount : '0'
        entry.comments = entry.comments ? entry.comments : ''

        entry.patientBalance = entry.patient
        entry.oldPatient = entry.patient
        entry.oldMedicalAid = entry.medicalAid
      }

      setItems(tmpItems)
      setIsModalVisible(false)
    } catch (e) {
      message.error(handleError(e, true))
    }
    setLoading(false)
  }

  // Calculate medicalAidTotal when items change
  useEffect(() => {
    let tmpMedicalAidTotal = 0

    for (const entry of items) {
      tmpMedicalAidTotal += parseFloat(entry.medicalAid)
    }

    setMedicalAidTotal(tmpMedicalAidTotal)
  }, [items])

  // Perform search when searchValue changes
  useEffect(() => {
    if (searchValue) {
      performSearch()
    }
    // eslint-disable-next-line
  }, [searchValue])

  return {
    loading,
    submitLoading,
    formData,
    items,
    invoiceRef,
    searchData,
    searchLoading,
    searchValue,
    invoices,
    isModalVisible,
    medicalAidTotal,
    setIsModalVisible,
    handleAllocatePayment,
    handleCloseTab,
    handleFieldChange,
    handleTableFieldChange,
    generateTotalsData,
    handleSearch,
    handleAccountChange,
    handleInvoiceSelect,
    setItems
  }
}
